import moment, {Moment} from "moment";
import {Vnode} from "mithril";
import m from "mithril";
import DatePicker from "./DatePicker";

export interface DateTimeInputBoxOptions {
    cssClass?: string, // ex. "dayOfMonth" or "minute"
    maxlength?: number, // maxlength of value in box
    min?: number, // min value in box
    max?: number, // max value in box
    step?: number, // step in box

    minDate?: Moment, // minimum date
    maxDate?: Moment, // maximum date
}

/**
 * One input box of datepicker
 *
 * |----------|
 * |    /\    |  <-- addCallback
 * |          |
 * |    ##    |  <-- Current value - dateFormatCallback
 * |          |
 * |    \/    |  <-- subCallback
 * |----------|
 *
 * On swipe up / down change value
 *
 * If value changed call dateChangedCallback
 */
export default class DateTimeInputBox {

    private static readonly SWIPE_SENSITIVITY = 0.1; // 0.1 ~ 10px of move to flip 1 day

    private touchStartPosition: number|null = null;
    private originalDate: Moment|null = null;
    private isFocused = false;

    constructor(
        private readonly datepicker: DatePicker,
        private readonly dateFormatCallback: (date: Moment|null) => string,
        private readonly modifyCallback: (date: Moment|null, n: number) => Moment,
        private readonly setterCallback: (date: Moment|null, value: number) => Moment,
        private readonly options: DateTimeInputBoxOptions = {}
    ) { }

    /**
     * Set date, respect minDate and maxDate
     * @param date
     */
    public set date(date: Moment|null) {
        if (date !== null) {
            if (this.options.minDate && this.options.minDate.diff(date) < 0) {
                // date is before minDate
                this.datepicker.date = this.options.minDate;
            } else if (this.options.maxDate && this.options.maxDate.diff(date) > 0) {
                // date is after maxDate
                this.datepicker.date = this.options.maxDate;
            } else {
                // date is in range
                this.datepicker.date = date;
            }
        } else {
            // date is not set yet
            this.datepicker.date = null;
        }
    }

    public get date() {
        return this.datepicker.date;
    }

    //------------------------._.-^
    // HANDLERS
    //
    private onTouchStart(e: TouchEvent) {
        this.touchStartPosition = e.touches[0].pageY;
        this.originalDate = this.date ? moment(this.date) : moment();
    }

    private onTouchMove(e: TouchEvent) {
        if (this.touchStartPosition === null || this.originalDate === null) {
            return;
        }
        const delta = Math.floor((e.changedTouches[0].pageY - this.touchStartPosition) * DateTimeInputBox.SWIPE_SENSITIVITY);
        if (delta === 0) {
            return;
        }

        e.preventDefault();
        this.date = this.modifyCallback(moment(this.originalDate), delta);
    }

    private onTouchEnd() {
        this.touchStartPosition = null;
        this.originalDate = null;
    }

    //------------------------._.-^
    // VIEW
    //
    public view(): Vnode {

        const getDateBoxClass = () => {
            let cssClass = '.date-box';
            if (this.options.cssClass) {
                cssClass += '.'+this.options.cssClass;
            }
            return cssClass;
        };

        return m(getDateBoxClass(), {
            ontouchstart: this.onTouchStart.bind(this),
            ontouchmove: this.onTouchMove.bind(this),
            ontouchend: this.onTouchEnd.bind(this),
        }, [
            m('.box-top',{}, this.viewArrow("add")),
            m('.box-middle',{}, this.viewInput()),
            m('.box-bottom',{}, this.viewArrow("sub")),
        ])

    }

    private viewArrow(direction: "add"|"sub"): Vnode {
        return m('.control.'+direction, {
            onclick: () =>  {
                this.date = this.modifyCallback(this.date, direction === "add" ? 1 : -1);
            }
        });
    }

    private viewInput(): Vnode {
        // Set required attributes
        const value = this.date === null ? '' : this.dateFormatCallback(this.date);

        const attributes: {[attribute:string]:any} = {
            type: "number",
            value: this.isFocused ? undefined : value,
            placeholder: this.dateFormatCallback(null),
            tabindex: 1,
            onblur: () => {
                this.isFocused = false;
            },
            onfocus: () => {
                this.isFocused = true;
            },
            onchange: (e: Event) => {
                const inputValue = this.cropInputValue( Number((<HTMLInputElement>e.currentTarget).value) );
                this.date = this.setterCallback( this.date, inputValue );
            },
            onkeydown: (e: KeyboardEvent) => {
                if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                    this.modifyCallback(this.date, e.key === "ArrowUp" ? 1 : -1);
                    (<HTMLInputElement>e.currentTarget).value = this.dateFormatCallback(this.date);
                }
            }

        };

        // Set optional attributes
        for (const a of ['min', 'max', 'step', 'maxlength']) {
            const value = ( <{[option:string]:any}> this.options)[a];
            if (value !== undefined) {
                attributes[a] = value;
            }
        }

        return m('input', attributes);
    }

    private cropInputValue(inputValue: number): number {
        if (this.options.max !== undefined) {
            inputValue = Math.min(this.options.max, inputValue);
        }
        if (this.options.min !== undefined) {
            inputValue = Math.max(this.options.min, inputValue);
        }
        return inputValue;
    }
}